import type { Invitation, PublicUser } from "@shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError, api, unwrap } from "@/lib/api";

export const usersKey = ["users"] as const;
export const invitationsKey = ["invitations"] as const;

export const usersQuery = () => ({
  queryKey: usersKey,
  queryFn: () => unwrap<PublicUser[]>(api.users.$get()),
});

export const invitationsQuery = () => ({
  queryKey: invitationsKey,
  queryFn: () => unwrap<Invitation[]>(api.invitations.$get()),
});

export const useUsers = () => useQuery(usersQuery());
export const useInvitations = () => useQuery(invitationsQuery());

export function useCreateInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => unwrap<Invitation>(api.invitations.$post({ json: { email } })),
    onSuccess: (inv) => {
      qc.setQueryData<Invitation[]>(invitationsKey, (old = []) =>
        old.some((i) => i.id === inv.id) ? old : [...old, inv],
      );
      toast.success(`${inv.email} can now sign up`);
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Could not create invitation"),
  });
}

export function useDeleteInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap<{ ok: true }>(api.invitations[":id"].$delete({ param: { id } })),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: invitationsKey });
      const snapshot = qc.getQueryData<Invitation[]>(invitationsKey);
      qc.setQueryData<Invitation[]>(invitationsKey, (old = []) => old.filter((i) => i.id !== id));
      return { snapshot };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.snapshot) qc.setQueryData(invitationsKey, ctx.snapshot);
      toast.error("Could not remove invitation");
    },
  });
}
